import { FC, useEffect, useState } from 'react';
import ReusableModal from '../common/Modal';
import {
  Box,
  Button,
  FormControl,
  TextField,
  Collapse,
  IconButton,
  Typography,
} from '@mui/material';
import { CaretDown, CaretRight } from '@phosphor-icons/react';
import { commonStyles } from '../../utils/styles/inputs';
import MessagePopup from '../common/MessagePopup';
import { useAlert } from '../../hooks/useAlert';
import { updateVersionHistoryPromptApi } from '../../utils/apis';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useDashboard } from '../../hooks/useDashboard';
import { SystemPromptData } from '../../schemas/apis/interface';
import CommitMessageModal from './CommitMessageModal';
import CofirmationModal from './CofirmationModal';

interface EditVersionHistoryModalProps {
  open: boolean;
  onClose: () => void;
  version?: any;
}

interface PromptField {
  key: string;
  label: string;
  placeholder: string;
}

const fields: PromptField[] = [
  {
    key: 'identity',
    label: 'Identity',
    placeholder: 'What persona do you want this AI to take?',
  },
  {
    key: 'firstMessage',
    label: 'Greetings',
    placeholder: 'The Opening line of AI assistant to start the conversation',
  },
  {
    key: 'context',
    label: 'Context',
    placeholder: 'Provide information about the situation or environment',
  },
  {
    key: 'job',
    label: 'Job',
    placeholder: "What's the task you want accomplished?",
  },
  {
    key: 'examples',
    label: 'Examples',
    placeholder: "Any references you'd like the AI to be guided by",
  },
  {
    key: 'constraints',
    label: 'Constraints',
    placeholder: "Boundaries you'd like the AI to stay within or go beyond",
  },
  {
    key: 'format',
    label: 'Format',
    placeholder: 'Request the answer in a table, email, list, etc.',
  },
  {
    key: 'tone',
    label: 'Tone',
    placeholder:
      'Examples: Friendly, professional, authoritative, informative, urgent',
  },
];

const EditVersionHistory: FC<EditVersionHistoryModalProps> = ({
  open,
  onClose,
  version,
}) => {
  const { selectedAssistant } = useDashboard();
  const { alert, setAlert, showAlert } = useAlert();
  const queryClient = useQueryClient();
  const [localValues, setLocalValues] = useState<any>({});
  const [originalValues, setOriginalValues] = useState<any>({});
  const [isCommitModalOpen, setIsCommitModalOpen] = useState(false);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);

  // State to track collapsed fields - by default all expanded
  const [collapsedFields, setCollapsedFields] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    if (version) {
      setLocalValues(version);
      setOriginalValues(version);
    }
  }, [version]);

  const handleInputChange = (key: string, value: string) => {
    setLocalValues((prev: any) => ({
      ...prev,
      [String(key)]: value,
    }));
  };

  const toggleFieldCollapse = (fieldKey: string) => {
    setCollapsedFields(prev => ({
      ...prev,
      [fieldKey]: !prev[fieldKey],
    }));
  };

  const hasUnsavedChanges = () => {
    return fields.some(field => {
      const originalValue = originalValues[field.key] || '';
      const currentValue = localValues[field.key] || '';
      return originalValue !== currentValue;
    });
  };

  const handleClose = () => {
    if (hasUnsavedChanges()) {
      setIsDiscardModalOpen(true);
    } else {
      onClose();
    }
  };

  const handleDiscardChanges = () => {
    setLocalValues(originalValues);
    setIsDiscardModalOpen(false);
    onClose();
  };

  const handleKeepEditing = () => {
    setIsDiscardModalOpen(false);
  };

  const { mutateAsync: updateAgentAsync, isPending: updateAgentPending } =
    useMutation({
      mutationKey: ['update-version-history-prompt'],
      mutationFn: updateVersionHistoryPromptApi,
      onSuccess: () => {
        queryClient.refetchQueries({ queryKey: ['get-version-history'] });
        queryClient.refetchQueries({
          queryKey: ['get-version-history-sidebar'],
        });
        queryClient.refetchQueries({ queryKey: ['agents'] });
        onClose();
        setIsCommitModalOpen(false);
        showAlert('Agent updated successfully', 'success');
      },
      onError: (error: any) => {
        showAlert(
          error?.response?.data?.message || 'Failed to update agent prompt',
          'error'
        );
      },
    });

  const handleSave = async (commitMessage: string) => {
    const filteredSystemPrompt = fields.reduce((acc, field) => {
      acc[field.key as keyof typeof acc] = localValues[field.key] || '';
      return acc;
    }, {} as Partial<SystemPromptData>);

    await updateAgentAsync({
      systemPrompt: filteredSystemPrompt as SystemPromptData,
      agentId: selectedAssistant?._id || '',
      name: selectedAssistant?.agentName || '',
      commitMessage,
    });
  };

  const handleSaveClick = () => {
    setIsCommitModalOpen(true);
  };

  return (
    <>
      <ReusableModal
        open={open}
        onClose={handleClose}
        closeOnBackdropClick={true}
        title={`Edit Version ${version?.version}`}
        width={1000}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {fields.map((field, index) => {
            const isCollapsed = collapsedFields[field.key];
            const hasValue =
              localValues[field.key] && localValues[field.key].trim() !== '';

            return (
              <FormControl fullWidth key={index}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '8px 4px',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  }}
                  onClick={() => toggleFieldCollapse(field.key)}
                >
                  <IconButton
                    size="small"
                    sx={{
                      padding: '2px',
                      marginRight: '8px',
                      color: '#666',
                    }}
                  >
                    {isCollapsed ? (
                      <CaretRight size={16} />
                    ) : (
                      <CaretDown size={16} />
                    )}
                  </IconButton>

                  <Typography
                    sx={{
                      ...commonStyles.formLabel,
                      margin: 0,
                      flexGrow: 1,
                      fontWeight: hasValue ? 600 : 400,
                      color: hasValue ? '#1976d2' : 'inherit',
                    }}
                  >
                    {field.label}
                  </Typography>

                  {hasValue && isCollapsed && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#666',
                        fontStyle: 'italic',
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {localValues[field.key]}
                    </Typography>
                  )}
                </Box>

                <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
                  <TextField
                    id={`field-${String(field.key)}`}
                    name={String(field.key)}
                    placeholder={field.placeholder}
                    fullWidth
                    variant="outlined"
                    value={localValues[String(field.key)] || ''}
                    onChange={e =>
                      handleInputChange(String(field.key), e.target.value)
                    }
                    multiline
                    minRows={1}
                    sx={{
                      marginTop: '8px',
                      marginBottom: '8px',
                      '& .MuiOutlinedInput-root': {
                        fontSize: '14px',
                        borderRadius: '6px',
                        '& fieldset': {
                          borderColor: '#E0E0E0',
                        },
                      },
                    }}
                  />
                </Collapse>
              </FormControl>
            );
          })}

          <Button
            variant="contained"
            sx={{
              width: 'fit-content',
              display: 'flex',
              justifyContent: 'center',
              textTransform: 'none',
              alignSelf: 'flex-end',
              marginTop: '10px',
              boxShadow: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: 'normal',
              letterSpacing: '-0.14px',
            }}
            onClick={handleSaveClick}
            disabled={updateAgentPending}
          >
            {`Save Changes as Version ${version?.version}`}
          </Button>
        </Box>
      </ReusableModal>

      <CommitMessageModal
        open={isCommitModalOpen}
        onClose={() => setIsCommitModalOpen(false)}
        onConfirm={handleSave}
        isLoading={updateAgentPending}
      />

      <CofirmationModal
        open={isDiscardModalOpen}
        onClose={handleKeepEditing}
        onConfirm={handleDiscardChanges}
        title="Discard Changes?"
        description="You have unsaved changes. Are you sure you want to discard them?"
        confirmButtonText="Discard"
        cancelButtonText="Keep Editing"
        isPending={false}
      />

      <MessagePopup alert={alert} setAlert={setAlert} />
    </>
  );
};

export default EditVersionHistory;
